import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from 'next/image';

const ImageModal = ({ themeMode, selectedImage, setSelectedImage }) => {
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && selectedImage) {
        setSelectedImage(null);
      }
    };
    setImageError(false);
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, setSelectedImage]);

  return (
    <AnimatePresence>
      {selectedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
            themeMode === "vintage" 
              ? "bg-amber-900/90 backdrop-blur-sm" 
              : "bg-black/90 backdrop-blur-md"
          }`}
          onClick={() => setSelectedImage(null)}
        >
          <motion.div 
            className={`relative max-w-4xl ${themeMode === "vintage" ? "border-4 border-amber-800 shadow-[8px_8px_0px_0px_rgba(146,64,14,0.2)]" : "shadow-2xl"} ${themeMode !== "vintage" ? "rounded-lg" : ""}`}
            initial={{ scale: 0.9, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }} 
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 25 }}
            onClick={e => e.stopPropagation()}
          >
            {imageError ? (
              <div className={`flex flex-col items-center justify-center p-8 min-w-[300px] min-h-[200px] ${
                themeMode === "vintage" 
                  ? "bg-amber-100 text-amber-900 border-4 border-amber-800" 
                  : "bg-gray-800 text-white rounded-lg"
              }`}>
                <p className="text-lg font-medium">Image could not be loaded.</p>
              </div>
            ) : (
              <div className="relative w-full max-h-[80vh] aspect-[auto] min-h-[200px]">
                <Image
                  src={selectedImage}
                  alt="Planning Document"
                  layout="fill"
                  objectFit="contain"
                  className={`${themeMode === "vintage" ? "" : "rounded-lg"}`}
                  onError={() => {
                    setImageError(true);
                  }}
                  priority
                  unoptimized
                />
              </div>
            )}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className={`absolute top-2 right-2 ${
                themeMode === "vintage" 
                  ? "bg-amber-800/50 text-amber-50" 
                  : "bg-black/50 text-white"
              } w-8 h-8 rounded-full flex items-center justify-center hover:bg-black/70 transition-colors`}
              onClick={() => setSelectedImage(null)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </motion.button>
            <div className={`absolute bottom-2 left-2 ${
              themeMode === "vintage" 
                ? "text-amber-100 bg-amber-800/50 px-2 py-1 uppercase text-xs tracking-wide" 
                : "text-white text-xs bg-black/50 px-2 py-1 rounded"
            }`}>
              Press ESC or click outside to close
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ImageModal;